import { Org, OrgLite } from 'types/org';
import {
  AllocationCRUDSchema,
  ConsumeStockEvent,
  ExpandedShipments,
  RelatedRecord,
  ShipmentLineLiteExpandedStockLot,
  StockLocation,
  StockLotCRUDSchema,
  Supplier,
} from 'types/inventory';
import { BomCRUDSchema, BomLineLite, BomLite } from 'types/bom';
import { User, UserLite } from 'types/user';
import {
  NoPartInventory,
  Part,
  PartCore,
  PartId,
  PartMarket,
  PartSpecs,
  PartSupply,
  Solicitation,
} from 'types/part';
import {
  KittingEstimate,
  LineItem,
  PriceModelLite,
  SnapshotLine,
} from 'types/purchasing';
import { Order, OrderLineLite, OrderSnapshotLine } from 'types/order';
import { PartRuleSet } from 'types/partRules';
import { TeamLite } from 'types/properties';
import { Lite } from 'types/api';
import {
  PurchaseRequest,
  PurchaseOrderLineServerSideSubSchema,
} from 'types/procurement';
import { CustomPropertiesType, DateTimeString } from 'types';
import { OrgPartType, V2OrgPartTypes } from 'types/clientV2/parts';
import { ProgressStackProps } from 'ui-component/ProgressStack';
import { PartSupplyRetryStatus } from 'types/partStatus';
import {
  DimensionClassOptions,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';
import { KitRequestLineStats } from 'hooks/useGetDecoratedKitRequestLines/utils';

export type ProductionRun = {
  id: string;
  status: string;
  name: string | null;
  referenceNumber: string;
  notificationEmail: string | null;
  notes: string;
  maxLead: number | null;
  requiredCertifications: string[];
  approvedSuppliers: string[] | Supplier[];
  limitToFacilities: StockLocation[] | null;
  archived: boolean;
  locked: boolean;
  createdAt: DateTimeString;
  approvedAt: string | null;
  allocatedAt: string | null;
  executedAt: string | null;
  // eslint-disable-next-line no-restricted-syntax -- API field name from external source
  cancelledAt: string | null;
  productionDate: string | null;
  autoGenerateAllocations: boolean;
  autoGenerateKitting: boolean;
  autoApproveKitting: boolean;
  autoExecute: boolean;
  authorizedOnly: boolean;
  addOverage: boolean;
  partRuleSet: PartRuleSet['id'];
  addOveragePerBom: boolean;
  addMultiKitOverage: number;
  noBackorder: boolean;
  avoidBackorder: boolean;
  includeExpectedStock: boolean;
  includeProcessingStock: boolean;
  includeOnhandStock: boolean;
  addRefdesNote: boolean;
  addBomLineNumberNote: boolean;
  lotMatchingPrioritization: string;
  org: OrgLite;
  approvedBy: UserLite;
  executedBy: UserLite;
  // eslint-disable-next-line no-restricted-syntax -- API field name from external source
  cancelledBy: UserLite;
  productionLocation: StockLocation | null;
  lines: ProductionRunLinesLite[];
  relatedRecords: RelatedRecord[];
  globalNotePrefix: string;
  globalNoteSuffix: string;
  defaultShippingService: string;
  defaultShippingCarrier: string;
  externalMetadata: {
    rowData?: { [key: string]: { [key: string]: string | number | null } };
  };
  team?: TeamLite | null;
  customProperties: CustomPropertiesType;
};

export type ProductionRunLite = Lite<ProductionRun, 'customProperties'>;

export type ProductionRunLines = {
  id: string;
  quant?: number;
  bom: BomLite;
  productionRun: ProductionRunLite;
};

export type ProductionRunLinesLite = Lite<ProductionRunLines, 'bom'>;

export type ProductionRunLineCreate = {
  bom: BomCRUDSchema['id'];
  quant?: number;
};

export type ProductionRunLineUpdate = {
  quant: number;
};

export type ProductionRunsRequest = {
  status?: string;
  productionLocationId?: StockLocation['id'];
  productionRunId?: ProductionRun['id'];
  bomId?: BomCRUDSchema['id'];
};

export type ProductionRunUpdate = {
  notes?: string;
  locked?: boolean;
  archived?: boolean;
  executed?: boolean;
  name?: string;
  referenceNumber?: string;
  productionDate?: string | null;
  productionLocation?: StockLocation['id'];
  autoGenerateAllocations?: boolean;
  autoGenerateKitting?: boolean;
  autoApproveKitting?: boolean;
  autoExecute?: boolean;
  globalNotePrefix?: string;
  globalNoteSuffix?: string;
  addBomLineNumberNote?: boolean;
  addOverage?: boolean;
  partRuleSet?: PartRuleSet['id'];
  addOveragePerBom?: boolean;
  addMultiKitOverage?: number;
  maxLead?: number;
  authorizedOnly?: boolean;
  requiredCertifications?: string[];
  approvedSuppliers?: string[];
  defaultShippingService?: string;
  defaultShippingCarrier?: string;
  bom?: BomCRUDSchema['id'] | null;
  noBackorder?: boolean;
  avoidBackorder?: boolean;
  externalMetadata?: object;
  internalMetadata?: object;
  customProperties?: object;
  team?: TeamLite['id'] | null;
};

export type ProductionRunCreate = {
  name: string;
  referenceNumber: string;
  productionDate?: string | null;
  notes?: string;
  productionLocation: StockLocation['id'];
  autoGenerateAllocations?: boolean;
  autoGenerateKitting?: boolean;
  autoApproveKitting?: boolean;
  autoExecute?: boolean;
  bom?: BomCRUDSchema['id'] | null;
  quant?: number;
  externalMetadata?: object;
  internalMetadata?: object;
  customProperties?: object;
  team?: TeamLite['id'] | null;
};

export type ProductionRunSnapshot = {
  id: string;
  lead: number;
  total: number;
  landedPrice: number;
  approved: boolean;
  summary: LineItem[];
  details: LineItem[];
  org: Org['id'] | Org;
  priceModel: PriceModelLite['id'] | PriceModelLite;
  submittedBy: User['id'] | User;
  approvedBy: null | User['id'] | User;
  expired: boolean;
  purchased: boolean;
  lines: SnapshotLine['id'][] | SnapshotLine[];
  relatedRecord: string;
  kittingEstimate?: KittingEstimate;
};

export type ProductionRunConsumption = {
  stockLot: StockLotCRUDSchema;
  consumedQuant: number;
};

export enum ProductionRunStatusOptions {
  draft = 'Draft',
  in_progress = 'In Progress',
  complete = 'Complete',
  // eslint-disable-next-line no-restricted-syntax -- copy is spelled correctly
  cancelled = 'Canceled',
  approved = 'Approved',
}

export type ProductionRunBomSummary = {
  id: string;
  total: number;
};

export type ProductionRunPart = {
  part: Part;
  alts: Part[];
  unapprovedAlts: Part[];
  id: string;
  quant: number;
  overage: number;
  total: number;
};

export type ProductionRunPartDescribeParts = {
  part: PartCore;
  alts: PartCore[];
  unapprovedAlts: PartCore[];
  id: string;
  quant: number;
  overage: number;
  total: number;
  partType?: OrgPartType;
} & V2OrgPartTypes;

export type ProductionRunPartsDescribeParts = {
  boms: BomLite[];
  bomLines: BomLineLite[];
  bomSummary: ProductionRunBomSummary[];
  partSummary: ProductionRunPartDescribeParts[];
};

export enum ProductionRunPartOverrideStatus {
  MANUFACTURER = 'manufacturer_provided',
  IGNORE = 'ignore',
  OTHER = 'other',
  QUANTITY = 'quantity',
}

export type ProductionRunPartOverride = {
  id: string;
  status: ProductionRunPartOverrideStatus;
  part: Part['id'];
  quant: number;
  productionRun: ProductionRun['id'];
};

export type ProductionRunPartOverrideCreate = {
  status: string;
  part: string;
  quant: number;
  productionRun: string;
};

type ProgramRowLoadingStatusTypes = {
  inventoryLoading: boolean;
  shipmentsLoading: boolean;
  allocationLoading: boolean;
};

export type PartTableRowDescribeParts = ProductionRunPartDescribeParts & {
  id: string;
  unallocated?: number;
  status?: string;
  lineStatus?: LineStatus;
  procurementStatus?: ProcurementStatusLine[];
  stockStatus?: StockStatusLine[];
  shipmentStatus?: ShipmentStatusLine[];
  kitStatus?: KitRequestLineStats['statusCounts'];
  supply?: PartSupply;
  specs?: PartSpecs;
  market?: PartMarket;
  altsSupply?: PartSupply[];
  altsMarket?: PartMarket[];
  minimumOverage: number;
  minimumTotal: number;
  currentTotal?: number;
  expected?: number;
  processing?: number;
  allocated?: number;
  onHand?: number;
  allocatedToRun?: number;
  consumed: number;
  atProductionLocation?: number;
  atProductionLocationProcessing?: number;
  stockByFacility?: NoPartInventory[];
  buyable?: number;
  quotable?: number;
  maybe?: number;
  minLead?: number;
  relatedBomLines?: BomLineLite[];
  relatedBoms?: BomLite[];
  externalMetadata?: { [key: string]: string | number | null };
  loadingStates?: ProgramRowLoadingStatusTypes;
  dimensionUnit?: UnitOfMeasureOptions;
  dimensionClass?: DimensionClassOptions;
  dimensionValue?: number;
};

export enum LineStatus {
  default = 'Include',
  ignore = 'Ignore',
  fromCM = 'From CM',
  fromOther = 'From Other',
}

export enum PartProcurementStatus {
  needToFind = 'Need to Find',
  maybe = 'Maybe',
  quotable = 'Quotable',
  quoteRequested = 'Quote Requested',
  buyable = 'Buyable',
  requested = 'Requested',
  processed = 'Processed',
  confirmed = 'Confirmed',
  shipped = 'Shipped',
  received = 'Received',
  loading = 'Loading',
}

export interface OrderLineByPart extends OrderLineLite {
  order: Order;
}

export interface ShipmentLineByPart {
  line: ShipmentLineLiteExpandedStockLot;
  shipment: ExpandedShipments;
}

export type ProcurementStatusLine = {
  status: PartProcurementStatus;
  quant: number;
  solicitations?: Solicitation[];
  orderLines?: OrderLineByPart[];
  purchaseRequests?: PurchaseRequest[];
  snapshotLines?: OrderSnapshotLine[];
  purchaseOrderLines?: PurchaseOrderLineServerSideSubSchema[];
};

export enum PartStockStatus {
  notInStock = 'Not in Stock',
  expected = 'Expected',
  processing = 'Processing',
  onHand = 'On Hand',
  atProductionProcessing = 'At Production (Processing)',
  atProductionOnHand = 'At Production (On Hand)',
  nonInventory = 'Non Inventory',
}

export type StockStatusLine = {
  status: PartStockStatus;
  quant: number;
};

export enum PartShipmentStatus {
  addedToKit = 'Added to Kit',
  notInShipment = 'Not in Shipment',
  draft = 'Draft',
  requested = 'Requested',
  pending = 'Pending',
  processing = 'Processing',
  picked = 'Picked',
  packed = 'Packed',
  shipped = 'Shipped',
  received = 'Received',
  ingested = 'Ingested',
  complete = 'Complete',
  nonInventory = 'Non Inventory',
}

export type ShipmentStatusLine = {
  status: PartShipmentStatus;
  quant: number;
  shipmentLines: ShipmentLineByPart[];
};

export type ProductionRunDates = {
  purchaseBy: string;
  onHandBy: string;
  shipBy: string;
  executeBy: string;
};

export type ProductionRunAllocationForPartRequest = {
  name?: string;
  part?: PartId;
  v2OrgPart?: V2OrgPartTypes['v2OrgPart'];
  quant: number;
  minimumQuant?: number;
  alts?: PartId[];
  altsV2OrgParts?: V2OrgPartTypes['altV2OrgParts'];
};

export type ProductionRunAllocationForPartPayload = {
  includeExpectedStock?: boolean;
  includeProcessingStock?: boolean;
  includeOnhandStock?: boolean;
  lotMatchingPrioritization?: string;
  limitToFacilities?: StockLocation['id'][];
  requests: ProductionRunAllocationForPartRequest[];
  name?: string;
};

export type ProductionRunAllocationForPart = {
  id: string;
  name: 'string' | null;
  status: string;
  releaseNotes: string | null;
  originalQuant: number;
  quant: number;
  releasedAt: string | null;
  org: Org['id'];
  stockLot: StockLotCRUDSchema['id'];
  releasedBy: string | null;
  relatedRecord: string;
  part?: PartId;
};

export type ConsumeStockForRunPayload = {
  allocation: AllocationCRUDSchema['id'];
  quant: number;
};

export type ProductionRunProps = {
  runData: {
    productionRun?: ProductionRun;
    isFetchingProductionRun: boolean;
    isLoadingProductionRun: boolean;
    isSuccessGetProductionRun: boolean;
  };
  linesData: {
    lines: ProductionRunLinesLite[];
    isLoadingLines: boolean;
  };
  partsData: {
    productionRunParts?: ProductionRunPartsDescribeParts;
    isFetchingProductionRunParts: boolean;
  };
  partOverridesData: {
    productionRunPartOverrides?: ProductionRunPartOverride[];
    isFetchingProductionRunPartOverrides: boolean;
  };
  stockLocationData: {
    stockLocations?: StockLocation[];
    isSuccessStockLocations: boolean;
  };
  row: {
    rowDataDescribeParts: PartTableRowDescribeParts[];
    isFetchingPartsData: boolean;
    solicitations?: Solicitation[];
    isFetchingInventoryProductionResponse: boolean;
    isFetchingShipments: boolean;
  };
  orgData: { activeOrgId?: Org['id']; org?: Org };
  stats: {
    approvedBomCount: number;
    partsToPurchase: number;
    existingShipmentPartsCount: number;
    partsNeedingQuotes: PartTableRowDescribeParts[];
    totalParts: number;
    allocatedPartsCount: number;
    partiallyAllocatedPartsCount: number;
    editingDisabled: boolean;
    bomsWithDifferentTeam?: ProductionRunLinesLite[];
    progressStackBarData?: ProgressStackProps['bars'];
    bomProgressStackBarData?: {
      bomId: string;
      bars: ProgressStackProps['bars'];
    }[];
  };
  consumeEvents: {
    consumeEvents: ConsumeStockEvent[];
    isFetchingConsumeEvents: boolean;
  };
  partSupplyRetryStatus: PartSupplyRetryStatus | null;
  totalNumberPartsWithSupplyLoadFailures: number;
};
